import {
  getOceanAgentStreamUrlFromRuntime,
  getSurfResearcherAgentIdFromRuntime,
  getSurfWhaleAgentIdFromRuntime,
  getCognitoIdentityPoolIdFromRuntime,
  getCognitoUserPoolIdFromRuntime,
  getAwsRegionFromRuntime,
} from "../config";

const oceanStore = {
  streamUrl: "",
  researcherAgentId: "",
  whaleAgentId: "",
  identityPoolId: "",
  userPoolId: "",
  awsRegion: "",
};

function viteEnv(key: string): string {
  const raw = import.meta.env as Record<string, string | undefined>;
  return typeof raw[key] === "string" ? raw[key]!.trim() : "";
}

export function applyOceanBootstrap(): void {
  const stream =
    getOceanAgentStreamUrlFromRuntime() || viteEnv("VITE_OCEAN_AGENT_STREAM_URL");
  const researcher =
    getSurfResearcherAgentIdFromRuntime() || viteEnv("VITE_SURF_RESEARCHER_AGENT_ID");
  const whale = getSurfWhaleAgentIdFromRuntime() || viteEnv("VITE_SURF_WHALE_AGENT_ID");
  const identityPool =
    getCognitoIdentityPoolIdFromRuntime() || viteEnv("VITE_COGNITO_IDENTITY_POOL_ID");
  const userPool = getCognitoUserPoolIdFromRuntime() || viteEnv("VITE_COGNITO_USER_POOL_ID");
  const region = getAwsRegionFromRuntime() || viteEnv("VITE_AWS_REGION");

  if (stream) oceanStore.streamUrl = stream;
  if (researcher) oceanStore.researcherAgentId = researcher;
  if (whale) oceanStore.whaleAgentId = whale;
  if (identityPool) oceanStore.identityPoolId = identityPool;
  if (userPool) oceanStore.userPoolId = userPool;
  if (region) oceanStore.awsRegion = region;
}

export function setOceanStreamUrl(url: string): void {
  oceanStore.streamUrl = url.trim();
}

export function setSurfResearcherAgentId(id: string): void {
  oceanStore.researcherAgentId = id.trim();
}

export function setSurfWhaleAgentId(id: string): void {
  oceanStore.whaleAgentId = id.trim();
}

export function setOceanIdentityPoolId(id: string): void {
  oceanStore.identityPoolId = id.trim();
}

export function setOceanUserPoolId(id: string): void {
  oceanStore.userPoolId = id.trim();
}

export function setOceanAwsRegion(region: string): void {
  oceanStore.awsRegion = region.trim();
}

export function getOceanAgentStreamUrl(): string {
  return oceanStore.streamUrl.replace(/\/+$/, "");
}

export function getSurfResearcherAgentId(): string {
  return oceanStore.researcherAgentId.trim();
}

/** Surf Whale orchestrator; falls back to researcher agent when whale id is unset. */
export function getSurfWhaleAgentId(): string {
  const whale = oceanStore.whaleAgentId.trim();
  if (whale) return whale;
  return getSurfResearcherAgentId();
}

export function hasSurfWhaleAgentId(): boolean {
  return Boolean(oceanStore.whaleAgentId.trim());
}

export function getOceanCognitoIdentityPoolId(): string {
  return oceanStore.identityPoolId.trim();
}

export function getOceanCognitoUserPoolId(): string {
  return oceanStore.userPoolId.trim();
}

export function getOceanAwsRegion(): string {
  return oceanStore.awsRegion.trim();
}

export function isSpeakerResearchConfigured(): boolean {
  return Boolean(
    getOceanAgentStreamUrl() &&
      getSurfResearcherAgentId() &&
      getOceanCognitoIdentityPoolId() &&
      getOceanCognitoUserPoolId() &&
      getOceanAwsRegion(),
  );
}

export type SpeakerWhaleConfigField =
  | "ocean_stream"
  | "whale_agent"
  | "cognito_identity"
  | "cognito_user_pool"
  | "aws_region";

export function getSpeakerWhaleConfigMissing(): SpeakerWhaleConfigField[] {
  const missing: SpeakerWhaleConfigField[] = [];
  if (!getOceanAgentStreamUrl()) missing.push("ocean_stream");
  if (!getSurfWhaleAgentId()) missing.push("whale_agent");
  if (!getOceanCognitoIdentityPoolId()) missing.push("cognito_identity");
  if (!getOceanCognitoUserPoolId()) missing.push("cognito_user_pool");
  if (!getOceanAwsRegion()) missing.push("aws_region");
  return missing;
}

export function speakerWhaleConfigMissingLabel(field: SpeakerWhaleConfigField): string {
  switch (field) {
    case "ocean_stream":
      return "ocean_agent_stream_url";
    case "whale_agent":
      return "surf_whale_agent_id (o surf_researcher_agent_id)";
    case "cognito_identity":
      return "cognito_identity_pool_id";
    case "cognito_user_pool":
      return "cognito_user_pool_id";
    case "aws_region":
      return "aws_region";
    default:
      return field;
  }
}

export function isSpeakerWhaleConfigured(): boolean {
  return getSpeakerWhaleConfigMissing().length === 0;
}
